import {
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { memo, useCallback, useMemo, useState, useRef } from "react";
import { SortRounded } from "../../../images/SortRoundedImg";
import { ScrapingAccount } from "../../../../types/apiTypes";
import { useVirtualizedList } from "../../../../hooks";
import { CustomCheckbox, InputWithDebounce } from "components/ui";

const ITEM_HEIGHT = 50;
const VIEWPORT_HEIGHT = 240;

const AccountsListItem = memo(
  ({ value, name, checked, onClick, children }: any) => (
    <MenuItem sx={{ padding: "4px" }} value={value} onClick={onClick}>
      <CustomCheckbox checked={checked} />
      <Stack flexDirection={"row"} gap={"8px"} flexGrow={1}>
        <ListItemText primary={name} />
        {children}
      </Stack>
    </MenuItem>
  ),
  (prev, next) => prev.checked === next.checked,
);

export const AccountsList = ({
  accounts,
  selectedIds,
  setSelectedIds,
  withSearch,
  withSelectAll,
  withHeader = true,
  withSort = true,
  withMissed = true,
  headerLeftTitle = "Account",
  headerRightTitle = "Missed",
}: {
  accounts: ScrapingAccount[];
  selectedIds: string[];
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
  withSearch?: boolean;
  withSelectAll?: boolean;
  withHeader?: boolean;
  withSort?: boolean;
  withMissed?: boolean;
  headerLeftTitle?: string;
  headerRightTitle?: string;
}) => {
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const containerRef = useRef<HTMLDivElement>(null);

  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) =>
      order === "asc" ? a.missed - b.missed : b.missed - a.missed,
    );
  }, [accounts, order]);

  const filtered = useMemo(
    () =>
      sortedAccounts.filter((acc) =>
        acc.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [sortedAccounts, search],
  );

  const { visibleItems, startIndex, containerHeight, handleScroll } =
    useVirtualizedList<ScrapingAccount>({
      items: filtered,
      itemHeight: ITEM_HEIGHT,
      viewportHeight: VIEWPORT_HEIGHT,
      searchValue: search,
      filterFn: () => true,
    });

  const toggleAll = useCallback(() => {
    if (selectedIds.length === accounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(accounts.map((account) => account.id));
    }
  }, [selectedIds.length, accounts]);

  const handleChange = useCallback(
    (id: string) => {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
      );
    },
    [setSelectedIds],
  );

  const memoizedOnClicks = useMemo(() => {
    const map = new Map<string, () => void>();
    accounts.forEach(({ id }) => {
      map.set(id, () => handleChange(id));
    });
    return map;
  }, [accounts, handleChange]);

  return (
    <Stack paddingTop="16px">
      {!withSearch && (
        <Stack direction="row" gap="8px" pr="4px" mb="12px">
          <InputWithDebounce value={search} setValue={setSearch} />
          {withSort && (
            <IconButton
              onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
              sx={{
                transform: order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <SortRounded />
            </IconButton>
          )}
        </Stack>
      )}

      {withHeader && (
        <Stack
          direction="row"
          justifyContent="space-between"
          px="10px"
          pb="4px"
        >
          <Typography variant="body1" color="text.secondary">
            {headerLeftTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {headerRightTitle}
          </Typography>
        </Stack>
      )}

      <Box
        ref={containerRef}
        onScroll={(e) => handleScroll(e.currentTarget.scrollTop)}
        sx={{
          position: "relative",
          overflowY: "auto",
          maxHeight: `${VIEWPORT_HEIGHT}px`,
        }}
      >
        <Box sx={{ height: `${containerHeight}px`, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: `${startIndex * ITEM_HEIGHT}px`,
              left: 0,
              right: 0,
            }}
          >
            {visibleItems.map(({ id, name, missed, value }) => (
              <AccountsListItem
                key={id}
                value={id}
                name={name}
                checked={selectedIds.includes(id)}
                onClick={memoizedOnClicks.get(id)!}
              >
                {withMissed && (
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    color="custom.textDarkBlue"
                  >
                    {missed}%
                  </Typography>
                )}
                {value && value}
              </AccountsListItem>
            ))}
          </Box>
        </Box>
      </Box>

      {visibleItems.length === 0 && (
        <Typography
          variant="body1"
          sx={{ color: "#797979", paddingLeft: "12px" }}
        >
          No company with such name found
        </Typography>
      )}

      {withSelectAll && visibleItems.length > 0 && (
        <>
          <Divider sx={{ color: "#D9D9D9", marginY: "8px" }} />
          <AccountsListItem
            name="Select All"
            checked={selectedIds.length === accounts.length}
            onClick={toggleAll}
          />
        </>
      )}
    </Stack>
  );
};
